import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Badge, Button, Card, Col, Form, Modal, Row, Spinner, Table } from "react-bootstrap";
import { FaExclamationTriangle, FaCheckCircle, FaFileUpload, FaFileInvoice, FaCloudUploadAlt, FaMoneyBillWave, FaEye, FaEdit, FaTrash, FaSearch, FaFilter } from "react-icons/fa";
import { actualizarCuentaPagar, eliminarCuentaPagar, obtenerAlertasCuentasPagar, obtenerComprasPorEmpresa, obtenerCuentasPagarPorEmpresa, registrarCompra } from "../../../services/contabilidad/comprasService";
import { subirArchivosFactura } from "../../../services/contabilidad/facturaStorageService";
import UploadFacturaExtractor from "./UploadFacturaExtractor";

const toNum = (v) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };
const aMonto = (v) => toNum(v).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const txt = (v) => String(v || "").trim().toUpperCase();

const docTipo = (x) => txt(x?.tipo || x?.tipo_comprobante || x?.comprobante?.tipo || x?._doc?.tipo || "FACTURA");
const docSerie = (x) => txt(x?.serie || x?.comprobante?.serie || x?._doc?.serie);
const docNumero = (x) => txt(x?.numero || x?.comprobante?.numero || x?._doc?.numero);

const claveComprobanteCxp = (x) => {
  const ruc = txt(x?.proveedor_ruc);
  const tipo = docTipo(x);
  const serie = docSerie(x);
  const numero = docNumero(x);
  if (!ruc || !serie || !numero) return "";
  return `${ruc}|${tipo}|${serie}|${numero}`;
};

const serieCorrelativoCxp = (x) => {
  const serie = docSerie(x);
  const numero = docNumero(x);
  if (!serie && !numero) return "-";
  return `${serie || "-"}-${numero || "-"}`;
};

const GestionCuentasPagar = ({ usuario }) => {
  const rucEmpresa = usuario?.ruc || "";
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [cxp, setCxp] = useState([]);
  const [compras, setCompras] = useState([]);
  const [alertas, setAlertas] = useState({ total: 0, vencidas: [], porVencer: [] });
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [facturasPendientes, setFacturasPendientes] = useState([]);
  const [savingFactura, setSavingFactura] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [savingAction, setSavingAction] = useState(false);
  const [busquedaCxp, setBusquedaCxp] = useState("");
  const [filtroCxp, setFiltroCxp] = useState("todos");
  const [editForm, setEditForm] = useState({
    id: "",
    proveedor: "",
    documento: "",
    oc_id: "",
    fecha_vencimiento: "",
    tipo_cambio: 1,
  });

  const recargar = useCallback(async () => {
    if (!rucEmpresa) return;
    try {
      setLoading(true);
      const [cxpData, comprasData, alertasData] = await Promise.all([
        obtenerCuentasPagarPorEmpresa(rucEmpresa),
        obtenerComprasPorEmpresa(rucEmpresa),
        obtenerAlertasCuentasPagar(rucEmpresa, 7),
      ]);
      setCxp(cxpData || []);
      setCompras(comprasData || []);
      setAlertas(alertasData || { total: 0, vencidas: [], porVencer: [] });
    } catch (e) { setError("No se pudo cargar cuentas por pagar."); }
    finally { setLoading(false); }
  }, [rucEmpresa]);

  useEffect(() => { recargar(); }, [recargar]);

  const resumen = useMemo(() => ({
    pendiente: cxp.reduce((acc, x) => acc + toNum(x.saldo_pendiente), 0),
    pagado: cxp.reduce((acc, x) => acc + toNum(x.monto_pagado), 0),
    vencidas: cxp.filter((x) => x.estado !== "PAGADO" && x.fecha_vencimiento && x.fecha_vencimiento < new Date().toISOString().slice(0, 10)).length,
  }), [cxp]);

  const comprasPorId = useMemo(() => {
    const map = new Map();
    (compras || []).forEach((c) => map.set(c.id, c));
    return map;
  }, [compras]);

  const cxpEnriquecido = useMemo(() => {
    return (cxp || []).map((x) => {
      const compra = comprasPorId.get(x?.compra_id);
      return {
        ...x,
        _doc: {
          tipo: x?.tipo || x?.tipo_comprobante || x?.comprobante?.tipo || compra?.comprobante?.tipo || "FACTURA",
          serie: x?.serie || x?.comprobante?.serie || compra?.comprobante?.serie || "",
          numero: x?.numero || x?.comprobante?.numero || compra?.comprobante?.numero || "",
        },
      };
    });
  }, [cxp, comprasPorId]);

  const clavesDuplicadas = useMemo(() => {
    const conteo = new Map();
    (cxpEnriquecido || []).forEach((x) => {
      const key = claveComprobanteCxp(x);
      if (!key) return;
      conteo.set(key, (conteo.get(key) || 0) + 1);
    });

    const duplicadas = new Set();
    conteo.forEach((cantidad, key) => {
      if (cantidad > 1) duplicadas.add(key);
    });
    return duplicadas;
  }, [cxpEnriquecido]);

  const cxpFiltrado = useMemo(() => {
    let rows = [...(cxpEnriquecido || [])];

    if (busquedaCxp.trim()) {
      const term = busquedaCxp.trim().toLowerCase();
      rows = rows.filter((x) => {
        const textoDoc = `${docTipo(x)} ${serieCorrelativoCxp(x)}`.toLowerCase();
        return (
          String(x?.proveedor_razon_social || "").toLowerCase().includes(term) ||
          String(x?.proveedor_ruc || "").toLowerCase().includes(term) ||
          String(x?.oc_id || "").toLowerCase().includes(term) ||
          textoDoc.includes(term)
        );
      });
    }

    const hoy = new Date().toISOString().slice(0, 10);
    if (filtroCxp === "pendiente") {
      rows = rows.filter((x) => txt(x?.estado) !== "PAGADO");
    } else if (filtroCxp === "pagado") {
      rows = rows.filter((x) => txt(x?.estado) === "PAGADO");
    } else if (filtroCxp === "duplicado") {
      rows = rows.filter((x) => clavesDuplicadas.has(claveComprobanteCxp(x)));
    } else if (filtroCxp === "vencido") {
      rows = rows.filter((x) => txt(x?.estado) !== "PAGADO" && x?.fecha_vencimiento && x.fecha_vencimiento < hoy);
    }

    return rows.sort((a, b) => {
      const getMs = (x) => {
        if (x?.created_at?.seconds) return x.created_at.seconds * 1000;
        if (x?.created_at?.toDate) return x.created_at.toDate().getTime();
        const dt = new Date(x?.fecha_emision || x?.fecha_vencimiento || 0).getTime();
        return Number.isNaN(dt) ? 0 : dt;
      };
      return getMs(b) - getMs(a);
    });
  }, [cxpEnriquecido, busquedaCxp, filtroCxp, clavesDuplicadas]);

  const eliminarDeCola = (id) => {
    setFacturasPendientes(prev => prev.filter(f => f.id !== id));
  };

  const handleFacturasExtraidas = (resultados) => {
    const lista = Array.isArray(resultados) ? resultados : [resultados];
    const nuevasFacturas = lista.map(factura => {
      const pRuc = txt(factura.proveedor?.ruc);
      const cTipo = docTipo(factura);
      const cSerie = docSerie(factura);
      const cNumero = docNumero(factura);
      const key = `${pRuc}|${cTipo}|${cSerie}|${cNumero}`;
      const yaExiste = key.length > 5 && cxpEnriquecido.some(x => claveComprobanteCxp(x) === key);

      return {
        id: Math.random().toString(36).substr(2, 9),
        proveedorRuc: factura.proveedor?.ruc || "",
        proveedorRazonSocial: factura.proveedor?.razonSocial || "",
        tipoComprobante: factura.comprobante?.tipo || "FACTURA",
        serie: factura.comprobante?.serie || "",
        numero: factura.comprobante?.numero || "",
        fechaEmision: factura.comprobante?.fechaEmision || "",
        fechaVencimiento: factura.comprobante?.fechaVencimiento || factura.comprobante?.fechaEmision || "",
        moneda: factura.comprobante?.moneda || "PEN",
        subtotal: factura.montos?.subtotal || 0,
        igv: factura.montos?.igv || 0,
        total: factura.montos?.total || 0,
        tipoGasto: "OPERATIVO",
        centroCosto: "",
        archivoOriginal: factura.archivoOriginal || null,
        tipoArchivoOriginal: factura.tipoArchivoOriginal || null,
        esDuplicada: yaExiste,
        estado: yaExiste ? "DUPLICADO" : "PENDIENTE"
      };
    });
    setFacturasPendientes(prev => [...prev, ...nuevasFacturas]);
    setShowUploadModal(false);

    const duplicadasCount = nuevasFacturas.filter(f => f.esDuplicada).length;
    if (duplicadasCount > 0) {
      setError(`⚠️ Atención: Se detectaron ${duplicadasCount} factura(s) que ya fueron registradas previamente en el sistema.`);
    } else {
      setSuccess(`${nuevasFacturas.length} factura(s) añadida(s) a la cola.`);
    }
  };

  const getPdfUrl = (x) =>
    x?.pdf_url ||
    x?.sunat?.pdf_url ||
    x?.comprobante?.pdf_url ||
    x?.documento_pdf_url ||
    "";

  const verPdf = (x) => {
    const pdfUrl = getPdfUrl(x);
    if (!pdfUrl) {
      setError("Este registro no tiene PDF adjunto.");
      return;
    }
    window.open(pdfUrl, "_blank", "noopener,noreferrer");
  };

  const abrirEditor = (x) => {
    setError("");
    setEditForm({
      id: x.id,
      proveedor: x.proveedor_razon_social || "",
      documento: serieCorrelativoCxp(x),
      oc_id: x.oc_id || "",
      fecha_vencimiento: x.fecha_vencimiento || "",
      tipo_cambio: toNum(x.tipo_cambio || 1),
    });
    setShowEditModal(true);
  };

  const guardarEdicion = async () => {
    if (!editForm.id) return;
    try {
      setSavingAction(true);
      setError("");
      await actualizarCuentaPagar({
        rucEmpresa,
        cuentaPagarId: editForm.id,
        cambios: {
          oc_id: editForm.oc_id,
          fecha_vencimiento: editForm.fecha_vencimiento,
          tipo_cambio: toNum(editForm.tipo_cambio || 1),
        },
        usuario: usuario?.correo || "sistema",
      });
      setShowEditModal(false);
      setSuccess("Registro actualizado correctamente.");
      await recargar();
    } catch (e) {
      setError(e?.message || "No se pudo actualizar el registro.");
    } finally {
      setSavingAction(false);
    }
  };

  const eliminarRegistro = async (x) => {
    const confirmado = window.confirm(`¿Eliminar el registro ${x?.serie || ""}-${x?.numero || ""}? Esta acción no se puede deshacer.`);
    if (!confirmado) return;
    try {
      setSavingAction(true);
      setError("");
      await eliminarCuentaPagar({
        rucEmpresa,
        cuentaPagarId: x.id,
        usuario: usuario?.correo || "sistema",
      });
      setSuccess("Registro eliminado correctamente.");
      await recargar();
    } catch (e) {
      setError(e?.message || "No se pudo eliminar el registro.");
    } finally {
      setSavingAction(false);
    }
  };

  const registrarTodoMasivo = async () => {
    if (facturasPendientes.length === 0) return;
    try {
      setSavingFactura(true);
      setError("");
      let completadosIds = [];
      let erroresCount = 0;
      for (let i = 0; i < facturasPendientes.length; i++) {
        const f = facturasPendientes[i];
        if (f.estado === "COMPLETADO") continue;
        try {
          setFacturasPendientes(prev => prev.map(item => item.id === f.id ? { ...item, estado: "PROCESANDO" } : item));
          let xmlUrl = "", pdfUrl = "";
          if (f.archivoOriginal) {
            const esXml = f.tipoArchivoOriginal === "xml";
            const urls = await subirArchivosFactura({
              rucEmpresa, proveedorRuc: f.proveedorRuc, serie: f.serie, numero: f.numero,
              archivoXml: esXml ? f.archivoOriginal : null, archivoPdf: !esXml ? f.archivoOriginal : null
            });
            xmlUrl = urls.xmlUrl || ""; pdfUrl = urls.pdfUrl || "";
          }
          await registrarCompra({
            rucEmpresa,
            compra: { ...f, subtotal: toNum(f.subtotal), igv: toNum(f.igv), total: toNum(f.total), usuarioRegistro: usuario?.correo || "", xmlUrl, pdfUrl }
          });
          completadosIds.push(f.id);
          setFacturasPendientes(prev => prev.map(item => item.id === f.id ? { ...item, estado: "COMPLETADO" } : item));
        } catch (err) {
          erroresCount++;
          const errorText = err?.message || "Factura duplicada o error al guardar";
          setFacturasPendientes(prev => prev.map(item => item.id === f.id ? { ...item, estado: "ERROR", errorMsg: errorText } : item));
        }
      }
      setTimeout(() => {
        setFacturasPendientes(prev => prev.filter(f => !completadosIds.includes(f.id)));
        if (completadosIds.length > 0) { setSuccess(`Se registraron ${completadosIds.length} factura(s) exitosamente.`); recargar(); }
        if (erroresCount > 0) { setError(`Se detectaron ${erroresCount} comprobante(s) duplicado(s) o con errores que no fueron registrados.`); }
      }, 1500);
    } catch (e) { setError("Error en el proceso masivo."); }
    finally { setSavingFactura(false); }
  };

  return (
    <div className="p-3">
      {error && <Alert variant="danger" onClose={() => setError("")} dismissible className="rounded-pill px-4 shadow-sm border-0">{error}</Alert>}
      {success && <Alert variant="success" onClose={() => setSuccess("")} dismissible className="rounded-pill px-4 shadow-sm border-0">{success}</Alert>}

      {showUploadModal && (
        <UploadFacturaExtractor
          onFacturaExtraida={handleFacturasExtraidas}
          onCancelar={() => setShowUploadModal(false)}
        />
      )}

      <Card className="border-0 shadow-sm mb-4" style={{ borderRadius: "15px", overflow: "hidden" }}>
        <Card.Header className="text-white py-3 d-flex justify-content-between align-items-center" style={{ background: "linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)", borderBottom: "3px solid #60a5fa" }}>
          <h6 className="mb-0 fw-bold d-flex align-items-center gap-2">
            <FaFileInvoice /> Registro de Facturas (Batch)
            {facturasPendientes.length > 0 && <Badge bg="light" text="dark" className="ms-2 rounded-pill">{facturasPendientes.length}</Badge>}
          </h6>
          <Button size="sm" className="rounded-pill px-3" style={{ background: "rgba(255,255,255,0.2)", border: "1px solid rgba(255,255,255,0.3)", color: "white" }} onClick={() => setShowUploadModal(true)}>
            <FaFileUpload className="me-2" /> Cargar (XML/PDF)
          </Button>
        </Card.Header>
        <Card.Body className="p-0">
          {facturasPendientes.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <FaCloudUploadAlt size="3rem" className="mb-3 opacity-25" />
              <h5>Cola de carga vacía</h5>
              <p className="small mb-0">Adjunta uno o varios archivos para comenzar.</p>
            </div>
          ) : (
            <>
              <div className="table-responsive" style={{ maxHeight: "400px" }}>
                <Table hover className="align-middle mb-0" style={{ fontSize: "0.85rem" }}>
                  <thead className="bg-light text-uppercase small fw-bold">
                    <tr>
                      <th className="px-3">Proveedor / Doc</th>
                      <th className="px-3">Fechas</th>
                      <th className="px-3 text-end">Importe</th>
                      <th className="px-3 text-center">Estado</th>
                      <th className="px-3 text-center"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {facturasPendientes.map((f) => (
                      <tr key={f.id} className={f.estado === "ERROR" ? "table-danger" : ""}>
                        <td className="px-3">
                          <div className="fw-bold text-dark">{f.proveedorRazonSocial}</div>
                          <div className="text-muted extra-small">{f.proveedorRuc} | {f.serie}-{f.numero}</div>
                        </td>
                        <td className="px-3 small">
                          <div>E: {f.fechaEmision}</div>
                          <div className="text-muted">V: {f.fechaVencimiento}</div>
                        </td>
                        <td className="px-3 text-end fw-bold">{f.moneda} {aMonto(f.total)}</td>
                        <td className="px-3 text-center">
                          {f.estado === "PENDIENTE" && <Badge bg="secondary">PENDIENTE</Badge>}
                          {f.estado === "PROCESANDO" && <Spinner size="sm" animation="border" variant="primary" />}
                          {f.estado === "ERROR" && <Badge bg="danger">ERROR</Badge>}
                        </td>
                        <td className="px-3 text-center">
                          <Button variant="link" className="text-danger p-0" onClick={() => eliminarDeCola(f.id)} disabled={f.estado === "PROCESANDO"}>✕</Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
              <div className="p-3 bg-light border-top d-flex justify-content-end">
                <Button variant="success" className="rounded-pill px-4 shadow-sm" onClick={registrarTodoMasivo} disabled={savingFactura || facturasPendientes.length === 0}>
                  {savingFactura ? <Spinner size="sm" animation="border" className="me-1" /> : <FaCheckCircle className="me-2" />} Registrar Todo
                </Button>
              </div>
            </>
          )}
        </Card.Body>
      </Card>

      <Row className="g-3 mb-4">
        {[
          { label: "Por Pagar", value: resumen.pendiente, icon: <FaMoneyBillWave />, color: "#1e40af", sub: "Saldo pendiente total" },
          { label: "Pagado", value: resumen.pagado, icon: <FaCheckCircle />, color: "#059669", sub: "Total histórico" },
          { label: "Vencidas", value: resumen.vencidas, icon: <FaExclamationTriangle />, color: "#dc2626", sub: "Urgente", isCount: true }
        ].map((kpi, i) => (
          <Col md={4} key={i}>
            <Card className="border-0 shadow-sm text-white" style={{ borderRadius: "15px", background: kpi.color, overflow: "hidden" }}>
              <Card.Body className="d-flex align-items-center p-3">
                <div style={{ background: "rgba(255,255,255,0.2)", width: "45px", height: "45px", borderRadius: "12px", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.5rem" }} className="me-3">{kpi.icon}</div>
                <div>
                  <div className="small opacity-75 text-uppercase fw-bold">{kpi.label}</div>
                  <div className="h4 mb-0 fw-bold">{kpi.isCount ? kpi.value : `S/ ${aMonto(kpi.value)}`}</div>
                  <div className="extra-small opacity-75">{kpi.sub}</div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      <Card className="border-0 shadow-lg" style={{ borderRadius: "20px", overflow: "hidden" }}>
        <Card.Header
          className="border-0 py-3 px-4"
          style={{ background: "linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)" }}
        >
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
            <h6 className="mb-0 text-white fw-bold">Cuentas por pagar registradas</h6>
            <small className="text-white-50">{cxpFiltrado.length} de {cxpEnriquecido.length} registro(s)</small>
          </div>
        </Card.Header>

        <Card.Body className="p-3" style={{ background: "linear-gradient(180deg, #f8fafc 0%, #ffffff 100%)" }}>
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" size="sm" className="me-2" />
              <span className="text-muted small">Cargando cuentas por pagar...</span>
            </div>
          ) : cxpEnriquecido.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <FaFileInvoice size="2rem" className="mb-2 opacity-50" />
              <div className="fw-semibold">Sin cuentas por pagar registradas</div>
              <small>Los documentos cargados aparecerán aquí.</small>
            </div>
          ) : (
            <>
              <Row className="mb-3 g-3">
                <Col md={6}>
                  <div className="position-relative">
                    <FaSearch className="position-absolute text-muted" style={{ left: "14px", top: "50%", transform: "translateY(-50%)" }} />
                    <Form.Control
                      type="text"
                      placeholder="Buscar proveedor, RUC, OC o documento..."
                      value={busquedaCxp}
                      onChange={(e) => setBusquedaCxp(e.target.value)}
                      className="ps-5 border-0 shadow-sm"
                      style={{ borderRadius: "12px", background: "#f1f5f9" }}
                    />
                  </div>
                </Col>
                <Col md={6}>
                  <div className="d-flex gap-2 flex-wrap justify-content-md-end">
                    {[
                      { key: "todos", label: "Todos", color: "#64748b" },
                      { key: "pendiente", label: "Pendientes", color: "#f59e0b" },
                      { key: "pagado", label: "Pagados", color: "#10b981" },
                      { key: "duplicado", label: "Duplicados", color: "#d97706" },
                      { key: "vencido", label: "Vencidos", color: "#ef4444" },
                    ].map(({ key, label, color }) => (
                      <Button
                        key={key}
                        size="sm"
                        className="rounded-pill px-3"
                        style={{
                          background: filtroCxp === key ? color : "transparent",
                          color: filtroCxp === key ? "white" : color,
                          border: `2px solid ${color}`,
                          fontWeight: 600,
                        }}
                        onClick={() => setFiltroCxp(key)}
                      >
                        <FaFilter className="me-1" size={10} /> {label}
                      </Button>
                    ))}
                  </div>
                </Col>
              </Row>

              {cxpFiltrado.length === 0 ? (
                <div className="text-center py-4 text-muted">
                  <div className="fw-semibold">No hay resultados con esos filtros</div>
                  <small>Prueba con otra búsqueda o cambia el estado.</small>
                </div>
              ) : (
                <div
                  className="table-responsive"
                  style={{ borderRadius: "16px", overflowY: "auto", maxHeight: "550px", boxShadow: "0 4px 15px rgba(0,0,0,0.05)" }}
                >
                  <Table
                    hover
                    className="align-middle mb-0"
                    style={{ fontSize: "0.9rem", borderCollapse: "separate", borderSpacing: 0 }}
                  >
                    <thead style={{ position: "sticky", top: 0, zIndex: 2, background: "linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)" }}>
                      <tr>
                        <th className="text-white py-3 px-3 border-0" style={{ background: "transparent" }}>Proveedor</th>
                        <th className="text-white py-3 px-3 border-0" style={{ background: "transparent" }}>Documento</th>
                        <th className="text-white py-3 px-3 border-0" style={{ background: "transparent" }}>Serie / Correlativo</th>
                        <th className="text-white py-3 px-3 border-0 text-center" style={{ background: "transparent" }}>Estado</th>
                        <th className="text-white py-3 px-3 border-0 text-end" style={{ background: "transparent" }}>Total</th>
                        <th className="text-white py-3 px-3 border-0 text-end" style={{ background: "transparent" }}>Saldo</th>
                        <th className="text-white py-3 px-3 border-0 text-center" style={{ background: "transparent" }}>Acción</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cxpFiltrado.map((x, idx) => (
                        <tr
                          key={x.id}
                          style={{ background: idx % 2 === 0 ? "#ffffff" : "#f8fafc", transition: "all 0.2s ease" }}
                          onMouseOver={(e) => { e.currentTarget.style.background = "#eff6ff"; }}
                          onMouseOut={(e) => { e.currentTarget.style.background = idx % 2 === 0 ? "#ffffff" : "#f8fafc"; }}
                        >
                          <td className="px-3 py-3 border-0">
                            <div className="fw-bold" style={{ color: "#1e3a8a" }}>{x.proveedor_razon_social}</div>
                            <div className="extra-small text-muted">{x.proveedor_ruc}</div>
                          </td>
                          <td className="px-3 py-3 border-0">
                            <Badge bg="light" text="dark" className="border" style={{ borderRadius: "999px", padding: "6px 12px" }}>{docTipo(x)}</Badge>
                            <div className="extra-small text-muted mt-1">Vence: {x.fecha_vencimiento || "-"}</div>
                          </td>
                          <td className="px-3 py-3 border-0">
                            <Badge bg="light" text="dark" className="border" style={{ borderRadius: "999px", padding: "6px 12px" }}>{serieCorrelativoCxp(x)}</Badge>
                          </td>
                          <td className="px-3 py-3 border-0 text-center">
                            {clavesDuplicadas.has(claveComprobanteCxp(x)) ? (
                              <Badge style={{ background: "#f59e0b", color: "#111827", fontWeight: 700, borderRadius: "999px", padding: "6px 12px" }}>DUPLICADO</Badge>
                            ) : (
                              <Badge
                                style={{
                                  background: x.estado === "PAGADO" ? "#10b981" : "#f59e0b",
                                  color: "white",
                                  fontWeight: 700,
                                  borderRadius: "999px",
                                  padding: "6px 12px"
                                }}
                              >
                                {x.estado}
                              </Badge>
                            )}
                          </td>
                          <td className="px-3 py-3 border-0 text-end fw-semibold">{x.moneda} {aMonto(x.total)}</td>
                          <td className="px-3 py-3 border-0 text-end fw-bold" style={{ color: "#2563eb" }}>{x.moneda} {aMonto(x.saldo_pendiente)}</td>
                          <td className="px-3 py-3 border-0 text-center">
                            <div className="d-flex justify-content-center gap-1">
                              <Button
                                size="sm"
                                className="border-0"
                                style={{ background: "#dbeafe", color: "#1d4ed8", borderRadius: "999px" }}
                                title="Ver PDF"
                                onClick={() => verPdf(x)}
                                disabled={savingAction || !getPdfUrl(x)}
                              >
                                <FaEye />
                              </Button>
                              <Button
                                size="sm"
                                className="border-0"
                                style={{ background: "#e5e7eb", color: "#374151", borderRadius: "999px" }}
                                title="Editar"
                                onClick={() => abrirEditor(x)}
                                disabled={savingAction}
                              >
                                <FaEdit />
                              </Button>
                              <Button
                                size="sm"
                                className="border-0"
                                style={{ background: "#fee2e2", color: "#dc2626", borderRadius: "999px" }}
                                title="Eliminar"
                                onClick={() => eliminarRegistro(x)}
                                disabled={savingAction}
                              >
                                <FaTrash />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </>
          )}
        </Card.Body>
      </Card>

      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="h6">Editar cuenta por pagar</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="small text-muted mb-3">
            <div><strong>Proveedor:</strong> {editForm.proveedor || "-"}</div>
            <div><strong>Documento:</strong> {editForm.documento || "-"}</div>
          </div>

          <Form.Group className="mb-3">
            <Form.Label className="small fw-semibold">OC</Form.Label>
            <Form.Control
              value={editForm.oc_id}
              onChange={(e) => setEditForm((p) => ({ ...p, oc_id: e.target.value }))}
              placeholder="Ej. OC-2026-001"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="small fw-semibold">Fecha vencimiento</Form.Label>
            <Form.Control
              type="date"
              value={editForm.fecha_vencimiento}
              onChange={(e) => setEditForm((p) => ({ ...p, fecha_vencimiento: e.target.value }))}
            />
          </Form.Group>

          <Form.Group>
            <Form.Label className="small fw-semibold">Tipo de cambio</Form.Label>
            <Form.Control
              type="number"
              step="0.0001"
              min="0"
              value={editForm.tipo_cambio}
              onChange={(e) => setEditForm((p) => ({ ...p, tipo_cambio: e.target.value }))}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowEditModal(false)}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={guardarEdicion} disabled={savingAction}>
            {savingAction ? <Spinner size="sm" animation="border" className="me-2" /> : null}
            Guardar cambios
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default GestionCuentasPagar;
